import { useEffect, useState } from "react";
import { Link, Outlet, useLocation } from "react-router";
import { Menu, X } from "lucide-react";
import { BrandMark } from "@/components/brand-mark";
import { WorkspaceSidebar } from "@/components/nav/workspace-sidebar";
import { ImpersonationBanner } from "@/features/super-admin/impersonation-banner";
import { OrgProvider } from "@/lib/org-context";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { GoogleCalendarConnectPrompt } from "@/features/integrations/google-calendar-connect-prompt";
import { MicrosoftTeamsConnectPrompt } from "@/features/integrations/microsoft-teams-connect-prompt";
import { ZoomConnectPrompt } from "@/features/integrations/zoom-connect-prompt";
import { WelcomeBannerArt } from "@/components/welcome-banner-art";

export function WorkspaceLayout() {
  const [mobileNav, setMobileNav] = useState(false);
  const location = useLocation();
  const [maintenance, setMaintenance] = useState<{
    maintenanceMode: boolean;
    message: string | null;
  } | null>(null);

  useEffect(() => {
    setMobileNav(false);
  }, [location.pathname]);

  useEffect(() => {
    void api<{ maintenanceMode: boolean; message: string | null }>(
      "/api/status",
    )
      .then(setMaintenance)
      .catch(() => undefined);
  }, []);

  return (
    <OrgProvider>
      <div className="rs-workspace flex min-h-screen flex-col bg-rs-bg">
        <ImpersonationBanner />
        <div className="flex min-h-0 flex-1">
          <div className="hidden md:block">
            <WorkspaceSidebar />
          </div>
          {mobileNav ? (
            <div className="fixed inset-0 z-40 flex md:hidden">
              <div className="flex w-60 flex-col overflow-y-auto bg-rs-sidebar shadow-rs">
                <div className="flex justify-end p-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Close Menu"
                    onClick={() => setMobileNav(false)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <WorkspaceSidebar />
              </div>
              <button
                type="button"
                className="flex-1 bg-black/40"
                aria-label="Dismiss Menu"
                onClick={() => setMobileNav(false)}
              />
            </div>
          ) : null}
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex items-center gap-2 border-b border-rs-border bg-rs-elevated px-3 py-2 md:hidden">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Open Menu"
                onClick={() => setMobileNav(true)}
              >
                <Menu className="h-4 w-4" />
              </Button>
              <Link
                to="/"
                className="text-inherit no-underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-rs-info"
                aria-label="AttendeeSync Dashboard"
                onClick={() => setMobileNav(false)}
              >
                <BrandMark size="sm" />
              </Link>
            </div>
            {maintenance?.maintenanceMode ? (
              <div
                className="border-b border-rs-warning bg-rs-warning-soft px-4 py-2 text-sm text-rs-warning"
                role="status"
              >
                {maintenance.message ||
                  "The platform is undergoing maintenance. Some actions may be unavailable."}
              </div>
            ) : null}
            <div className="relative flex-1">
              {/* Persistent ribbon banner across all workspace pages */}
              <div className="rs-welcome-art-bg" aria-hidden="true">
                <WelcomeBannerArt />
              </div>
              <main className="relative z-10 flex-1 px-4 py-6 sm:px-6 lg:px-8">
                <Outlet />
              </main>
              <GoogleCalendarConnectPrompt />
              <ZoomConnectPrompt />
              <MicrosoftTeamsConnectPrompt />
            </div>
          </div>
        </div>
      </div>
    </OrgProvider>
  );
}
